'use client';

import { FC, useMemo, useContext, useEffect, useState } from 'react';
import { ClientProfileEditorProps } from './ClientProfileEditor.types';
import { timeZones } from '@/_constants/time-zones';
import {
  Button,
  ButtonGroup,
  Card,
  Flex,
  Input,
  Select,
  SelectItem,
  SplitView,
  ToggleCard,
  ChangeLoginMethodCard,
} from '@/_components';
import { UserContext } from '@/_contexts/UserProvider';
import { ToastContext } from '@/_contexts/ToastProvider';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import { updateUser, type UpdateUserAttrs } from '@/_api/users';
import { deformatPhoneNumber, formatPhoneNumber } from '@/_utils/phoneNumbers';
import { useParams } from 'next/navigation';
import { UserPreviewContext } from '@/_contexts/admin/UserPreviewProvider';
import type { User } from '@/_types/models/user';

const PREFERRED_CONTACT_METHODS: Array<SelectItem> = [
  { name: 'Email', value: 'email' },
  { name: 'Email & Text', value: 'sms' },
];

export const ClientProfileEditor: FC<ClientProfileEditorProps> = () => {
  const { user, fullName, token, setUser, isUserReadOnly } =
    useContext(UserContext);
  const { firm } = useContext(FirmSettingsContext);
  const { addToast } = useContext(ToastContext);
  const { previewUser, previewFullName } = useContext(UserPreviewContext);

  const { previewUserId } = useParams();

  const clientProfileUser: User | undefined = useMemo(
    () => (!!previewUserId ? previewUser : user),
    [previewUserId, previewUser, user],
  );

  const clientProfileFullName: string = useMemo(
    () => (!!previewUserId ? previewFullName : fullName),
    [previewUserId, previewFullName, fullName],
  );

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [email, setEmail] = useState<string>('');
  // const [workEmail, setWorkEmail] = useState<string>('');
  const [workPhone, setWorkPhone] = useState<string>('');
  const [workPhoneExt, setWorkPhoneExt] = useState<string>('');
  const [contactMethod, setContactMethod] = useState<string>('');
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [timeZone, setTimeZone] = useState<string>('');

  const [twoFactor, setTwoFactor] = useState<boolean>(false);
  const [hideConversationInEmail, setHideConversationInEmail] =
    useState<boolean>(
      clientProfileUser?.preferences?.hide_conversation_in_email ?? false,
    );
  const firmHideConversationInEmail =
    !!firm?.preferences?.hide_conversation_in_email;

  const isFormDisabled: boolean =
    !email || !!previewUserId || isLoading || isUserReadOnly;

  const newUserAttrs: UpdateUserAttrs = {
    enabled_2fa: twoFactor,
    preferred_contact_method: contactMethod,
    email: email || clientProfileUser?.email || '',
    phone_number: deformatPhoneNumber(phoneNumber),
    time_zone: timeZone,
    userId: clientProfileUser?.id!,
    work_email: clientProfileUser?.work_email!,
    work_phone_number: deformatPhoneNumber(workPhone),
    work_phone_extension: workPhoneExt,
    preferences: {
      hide_conversation_in_email: hideConversationInEmail,
    },
  };

  const onSaveProfile = async (event?: React.FormEvent) => {
    event?.preventDefault?.();

    if (!clientProfileUser) return;

    try {
      setIsLoading(true);

      const updatedUser = await updateUser({
        token,
        ...newUserAttrs,
      });

      setUser({
        ...clientProfileUser,
        ...updatedUser,
      });

      addToast('success', 'Profile updated successfully');
    } catch (error) {
      addToast('error', 'Failed to update profile');
    } finally {
      setIsLoading(false);
    }
  };

  const onEmailChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setEmail(event.target.value);
  // const onWorkEmailChange = (event: React.ChangeEvent<HTMLInputElement>) =>
  //   setWorkEmail(event.target.value);
  const onContactMethodChange = (event: React.ChangeEvent<HTMLSelectElement>) =>
    setContactMethod(event.target.value);
  const onPhoneNumberChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setPhoneNumber(event.target.value);
  const onWorkPhoneChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setWorkPhone(event.target.value);
  const onWorkPhoneExtChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setWorkPhoneExt(event.target.value);
  const onTimeZoneChange = (event: React.ChangeEvent<HTMLSelectElement>) =>
    setTimeZone(event.target.value);

  const onHideConversationInEmailToggle = async () => {
    if (!clientProfileUser) return;

    const newHideState: boolean = !hideConversationInEmail;

    try {
      const updatedUser = await updateUser({
        token,
        userId: user?.id!,
        preferences: {
          hide_conversation_in_email: newHideState,
        },
      });

      if (!updatedUser?.error) {
        setUser({
          ...clientProfileUser,
          ...updatedUser,
        });
        setHideConversationInEmail(newHideState);
      }

      addToast('success', 'Profile updated successfully');
    } catch (error) {
      addToast('error', 'Failed to update profile');
    }
  };

  useEffect(() => {
    if (user) {
      setEmail(clientProfileUser?.email || '');
      // setWorkEmail(clientProfileUser?.work_email || '');
      setWorkPhone(
        formatPhoneNumber(clientProfileUser?.work_phone_number || ''),
      );
      setWorkPhoneExt(clientProfileUser?.work_phone_extension || '');
      setContactMethod(clientProfileUser?.preferred_contact_method || '');
      setPhoneNumber(formatPhoneNumber(clientProfileUser?.phone_number || ''));
      setWorkPhone(
        formatPhoneNumber(clientProfileUser?.work_phone_number || ''),
      );
      setWorkPhoneExt(clientProfileUser?.work_phone_extension || '');

      if (clientProfileUser?.time_zone) {
        setTimeZone(clientProfileUser.time_zone);
      } else if (firm?.time_zone) {
        setTimeZone(firm.time_zone);
      }

      setTwoFactor(!!clientProfileUser?.enabled_2fa);
    }
    // eslint-disable-next-line
  }, [firm?.time_zone, clientProfileUser?.id]);

  useEffect(() => {
    setHideConversationInEmail(
      clientProfileUser?.preferences?.hide_conversation_in_email ?? false,
    );
  }, [clientProfileUser?.preferences?.hide_conversation_in_email]);

  // TODO(API): @chris work_phone doesn't save on users patch
  return (
    <Flex gap={12} responsive="desktop">
      <SplitView>
        <Card header={clientProfileFullName} headerIcon="user">
          <Flex isColumn={true} gap={10} padding={10}>
            <Flex gap={8}>
              <Input
                isDisabled={isFormDisabled}
                label="Primary Contact Email"
                value={email}
                placeholder="john@example.com"
                onChange={onEmailChange}
              />
              {/* <Input
                label="Work Email"
                value={workEmail}
                isDisabled={isFormDisabled}
                placeholder="john@example.com"
                onChange={onWorkEmailChange}
              /> */}
            </Flex>
            <Flex gap={8}>
              <Select
                items={PREFERRED_CONTACT_METHODS}
                selectedValue={contactMethod}
                label="Contact Method"
                isDisabled={isFormDisabled}
                onChange={onContactMethodChange}
              />
              <Input
                label="Phone Number"
                value={phoneNumber}
                isDisabled={isFormDisabled}
                mask="(___) ___-____"
                placeholder="(999) 999-9999"
                onChange={onPhoneNumberChange}
              />
            </Flex>
            <Flex gap={8}>
              <Input
                label="Work Phone Number"
                value={workPhone}
                mask="(___) ___-____"
                isDisabled={isFormDisabled}
                placeholder="(999) 999-9999"
                onChange={onWorkPhoneChange}
              />
              <Input
                label="Work Phone Extension"
                value={workPhoneExt}
                isDisabled={isFormDisabled}
                placeholder="000"
                onChange={onWorkPhoneExtChange}
              />
            </Flex>
            <Flex className="wp-100">
              <Select
                items={timeZones.map((tz) => ({ name: tz, value: tz }))}
                isDisabled={isFormDisabled}
                label="Time Zone"
                onChange={onTimeZoneChange}
                selectedValue={timeZone}
              />
            </Flex>
            <ButtonGroup align="right">
              <Button
                intent="primary"
                isDisabled={isFormDisabled}
                isLoading={isLoading}
                value="Update Profile"
                iconLeft="check"
                onClick={onSaveProfile}
              />
            </ButtonGroup>
          </Flex>
        </Card>
      </SplitView>
      <SplitView>
        <ChangeLoginMethodCard />
        <ToggleCard
          title="Hide Conversation Content From Email"
          isChecked={hideConversationInEmail || firmHideConversationInEmail}
          isDisabled={isFormDisabled || firmHideConversationInEmail}
          onToggle={onHideConversationInEmailToggle}
          description={
            firmHideConversationInEmail
              ? 'Emails sent to me regarding conversations will not include the content. Enabled by firm for all users.'
              : 'Emails sent to me regarding conversations will not include the content.'
          }
        />
      </SplitView>
    </Flex>
  );
};
